"use client";

import { useState, useEffect, useRef } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import NewsCard from "@/components/shared/NewsCard";
import Pagination from "@/components/ui/pagination";
import { getImageUrl } from "@/utils/utils";
import { useClickOutside } from "@/hooks/useClickOutside";
import Image from "next/image";
import { ALL_PROJECTS_STATUS } from "@/data/shared";

const projectTypes = [ALL_PROJECTS_STATUS, "Ongoing", "Completed"];

interface ServiceList {
  id: number;
  documentId: string;
  title: string;
  excerpt: null;
  description: string;
  slug: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
}

interface ProjectsItem {
  id: number;
  documentId: string;
  title: string;
  slug: string;
  excerpt: string | null;

  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  image: {
    id: number;
    documentId: string;
    name: string;
    alternativeText: string | null;
    url: string;
  } | null;
}

interface ProjectsPageClientProps {
  lang: string;
  locale: string;
  title: string;
  serviceList?: ServiceList[] | null;
  initialProjects: ProjectsItem[];
  initialPagination: {
    page: number;
    pageSize: number;
    pageCount: number;
    total: number;
  };
  initialSelectedType: string;
  initialSelectedService: string;
  initialCurrentPage: number;
  projectsError?: string | null;
}

export const ALL_SERVICES = {
  id: 0,
  documentId: "-",
  title: "All",
  excerpt: null,
  description: "",
  slug: "all-services",
  createdAt: "",
  updatedAt: "",
  publishedAt: "",
};

export default function ProjectsPageClient({
  lang,
  locale,
  title,
  serviceList = [ALL_SERVICES],
  initialProjects,
  initialPagination,
  initialSelectedType,
  initialSelectedService,
  initialCurrentPage,
  projectsError,
}: ProjectsPageClientProps) {
  const router = useRouter();
  const searchParams = useSearchParams();

  // Find the selected service from serviceList based on initialSelectedService
  const findSelectedService = (serviceId: string) => {
    if (serviceId === "all-services") return ALL_SERVICES;
    // Try to find by documentId first, then by slug
    return (
      serviceList?.find(
        (service) =>
          service.documentId === serviceId || service.slug === serviceId
      ) ||
      serviceList?.[0] ||
      ALL_SERVICES
    );
  };

  const [selectedService, setSelectedService] = useState<ServiceList | null>(
    findSelectedService(initialSelectedService)
  );
  const [selectedType, setSelectedType] = useState<string>(
    initialSelectedType === ALL_PROJECTS_STATUS
      ? ALL_PROJECTS_STATUS
      : initialSelectedType?.split(" ")?.[0]
  );
  const [currentPage, setCurrentPage] = useState(initialCurrentPage);
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const [projectsData, setProjectData] =
    useState<ProjectsItem[]>(initialProjects);
  const [totalPages, setTotalPages] = useState(initialPagination.pageCount);

  // Use ref to track if we're in the middle of a user-initiated change
  const isUserChangingRef = useRef(false);

  // Add ref to scroll to content section on pagination
  const contentSectionRef = useRef<HTMLDivElement>(null);

  // Update data when props change without causing scroll jumps
  useEffect(() => {
    // Only update if the data has actually changed
    if (JSON.stringify(initialProjects) !== JSON.stringify(projectsData)) {
      setProjectData(initialProjects);
    }
    if (initialPagination.pageCount !== totalPages) {
      setTotalPages(initialPagination.pageCount);
    }
  }, [initialProjects, initialPagination.pageCount, projectsData, totalPages]);

  // Update selected service when props change (but not during user interactions)
  useEffect(() => {
    if (!isUserChangingRef.current) {
      const newService = findSelectedService(initialSelectedService);
      if (newService?.documentId !== selectedService?.documentId) {
        setSelectedService(newService);
      }
    }
  }, [initialSelectedService, selectedService]);

  // Update selected type when props change (but not during user interactions)
  useEffect(() => {
    if (!isUserChangingRef.current && initialSelectedType !== selectedType) {
      setSelectedType(
        initialSelectedType === ALL_PROJECTS_STATUS
          ? ALL_PROJECTS_STATUS
          : initialSelectedType?.split(" ")?.[0]
      );
    }
  }, [initialSelectedType, selectedType]);

  // Update current page when props change (but not during user interactions)
  useEffect(() => {
    if (!isUserChangingRef.current && initialCurrentPage !== currentPage) {
      setCurrentPage(initialCurrentPage);
    }
  }, [initialCurrentPage, currentPage]);

  // Sync state with URL parameters when they change (but not during user interactions)
  useEffect(() => {
    if (!isUserChangingRef.current) {
      const typeParam = searchParams.get("type");
      const serviceParam = searchParams.get("service");
      const pageParam = searchParams.get("page");

      if (typeParam && typeParam !== selectedType) {
        setSelectedType(
          typeParam === ALL_PROJECTS_STATUS
            ? ALL_PROJECTS_STATUS
            : typeParam?.split(" ")?.[0]
        );
      }

      if (serviceParam) {
        const newService = findSelectedService(serviceParam);
        if (newService?.documentId !== selectedService?.documentId) {
          setSelectedService(newService);
        }
      }

      if (pageParam && parseInt(pageParam) !== currentPage) {
        setCurrentPage(parseInt(pageParam));
      }
    }
  }, [searchParams, selectedType, currentPage, selectedService]);

  // Add click outside functionality for dropdown
  const typeDropdownRef = useClickOutside<HTMLDivElement>(() => {
    setTypeDropdownOpen(false);
  });

  // Remove automatic scroll to top to prevent scroll jumps
  // useEffect(() => {
  //   scrollTo(0, 0);
  // }, []);

  // Navigation handler for filters and pagination
  const handleFilterChange = (
    newType: string,
    newService: ServiceList | null,
    newPage: number = 1
  ) => {
    const urlParams = new URLSearchParams();

    if (newType !== ALL_PROJECTS_STATUS) {
      urlParams.set("type", newType);
    }

    if (newService && newService?.title !== ALL_SERVICES?.title) {
      urlParams.set("service", newService.documentId);
    }

    if (newPage > 1) {
      urlParams.set("page", newPage.toString());
    }

    const queryString = urlParams.toString();
    const newUrl = queryString
      ? `/${locale}/projects?${queryString}`
      : `/${locale}/projects`;

    router.replace(newUrl, { scroll: false });
  };

  // Handle service selection
  const handleServiceChange = (service: ServiceList) => {
    // Mark that user is changing to prevent prop overrides
    isUserChangingRef.current = true;

    setSelectedService(service);
    handleFilterChange(selectedType, service, 1);

    // Reset the flag after URL update completes
    setTimeout(() => {
      isUserChangingRef.current = false;
    }, 200);
  };

  // Handle type selection
  const handleTypeChange = (type: string) => {
    // Mark that user is changing to prevent prop overrides
    isUserChangingRef.current = true;

    // Update local state immediately
    setSelectedType(type);

    // Update URL
    handleFilterChange(type, selectedService, 1);

    // Reset the flag after URL update completes
    setTimeout(() => {
      isUserChangingRef.current = false;
    }, 200);
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    handleFilterChange(selectedType, selectedService, page);

    // Scroll to the top of the content section when page changes
    // Use setTimeout to ensure DOM has updated after navigation
    setTimeout(() => {
      if (contentSectionRef.current) {
        contentSectionRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 100);
  };

  const displayedProjects = projectsData || [];

  const noResultsMessage = "No data found!";

  // Show error state if projects data fetching failed
  if (projectsError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 text-lg mb-4">{projectsError}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-[50px] pb-20 group/progress">
      <div className="flex flex-col items-center gap-4 lg:gap-[17px] mb-8 sm:mb-12 lg:mb-[50px]">
        <motion.h2
          className="text-[#0a1119] text-center text-2xl sm:text-3xl lg:text-[40px] font-medium leading-tight lg:leading-[58px] lg:tracking-[-1.44px]"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          {title || "Our Projects"}
        </motion.h2>
        <div className="w-[50px] h-[2px] bg-[#1a5852]"></div>
      </div>
      <div className="bg-white relative overflow-hidden">
        {/* Background Pattern */}
        <Image
          src="/images/shared/page-blend-bg.svg"
          alt="Background pattern"
          fill
          className="object-cover opacity-30 luminosity mix-blend-luminosity pointer-events-none select-none"
        />

        <div
          ref={contentSectionRef}
          className="relative p-4 sm:p-6 lg:p-[50px] lg:min-h-[682px]"
        >
          {/* Header Controls */}
          {/* Project Type Dropdown */}
          <div
            className="relative w-full sm:w-auto flex justify-end mb-6"
            ref={typeDropdownRef}
          >
            <button
              onClick={() => setTypeDropdownOpen(!typeDropdownOpen)}
              className="flex items-center justify-between w-full lg:w-[200px] px-4 py-3 border border-[#dee1e5] rounded bg-white text-[#0a1119] text-base cursor-pointer"
            >
              <span>{selectedType}</span>
              <ChevronDown className="w-[14px] h-[14px] text-[#88909f]" />
            </button>

            {typeDropdownOpen && (
              <div className="absolute top-full right-0 mt-1 w-full lg:w-[200px] bg-white border border-[#dee1e5] rounded shadow-lg z-10 overflow-y-auto max-h-[250px]">
                {projectTypes.map((type) => (
                  <button
                    key={type}
                    onClick={() => {
                      handleTypeChange(type);
                      setTypeDropdownOpen(false);
                    }}
                    className="w-full px-4 py-3 text-left hover:bg-[#f4f5f6] text-[#0a1119] text-base cursor-pointer"
                  >
                    {type}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-wrap mx-auto justify-center items-center gap-x-2 gap-y-3 sm:gap-x-4 sm:gap-y-4 lg:gap-x-8 lg:gap-y-2 mb-8">
            {/* Dynamic Services */}
            {(serviceList || []).map((category, index) => (
              <button
                key={index}
                onClick={() => handleServiceChange(category)}
                className={`relative max-w-[155px] md:max-w-[140px] text-sm sm:text-base font-medium capitalize transition-colors cursor-pointer before:content-[''] before:absolute before:bottom-[-2px] before:left-0 before:w-0 before:h-[2px] before:bg-[#1a5852] before:transition-all before:duration-300 before:ease-out hover:before:w-full ${
                  selectedService?.documentId === category?.documentId
                    ? "text-[#1a5852]"
                    : "text-[#0a1119] hover:text-[#1a5852]"
                }`}
              >
                <span
                  className="block overflow-hidden text-ellipsis whitespace-nowrap w-full"
                  title={category?.title}
                >
                  {category?.title}
                </span>
                {selectedService?.documentId === category?.documentId && (
                  <div className="absolute bottom-[-2px] left-0 w-full h-[2px] bg-[#1a5852]"></div>
                )}
              </button>
            ))}
          </div>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8"
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: {
                  staggerChildren: 0.1,
                },
              },
            }}
          >
            {/* Projects Grid */}
            {displayedProjects.map((project) => (
              <motion.div
                key={project.id}
                variants={{
                  hidden: { opacity: 0, y: 30 },
                  visible: { opacity: 1, y: 0 },
                }}
                transition={{ duration: 0.5 }}
              >
                <NewsCard
                  image={getImageUrl(project.image?.url || "")}
                  title={project.title}
                  excerpt={project.excerpt || ""}
                  alt={project.title}
                  href={`/${lang}/projects/${project.slug}`}
                />
              </motion.div>
            ))}
          </motion.div>

          {/* Empty State */}
          {displayedProjects.length === 0 && (
            <div className="text-center flex items-center justify-center h-[346px] md:h-[448px] lg:h-[500px]">
              <p className="text-[#88909f] text-lg">{noResultsMessage}</p>
            </div>
          )}

          {/* Pagination with 32px gap from news cards */}
          {totalPages > 1 && (
            <div style={{ marginTop: "32px" }}>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </div>

        {/* Progress Bar */}
        <div className="flex h-[2px] bg-gray-25 relative">
          <div
            className={`h-[2px] bg-[#1c8579] absolute left-0 top-0 transition-all duration-500 w-0 group-hover/progress:w-full`}
          ></div>
        </div>
      </div>
    </div>
  );
}
